import * as React from "react"
import { Check } from "lucide-react"

interface CheckboxProps extends React.InputHTMLAttributes<HTMLInputElement> {
  id?: string;
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
}

const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  ({ className, checked: controlledChecked, onCheckedChange, onChange, ...props }, ref) => {
    const internalRef = React.useRef<HTMLInputElement>(null);
    const resolvedRef = ref || internalRef;

    // For uncontrolled checkbox when no parent is controlling state
    const [uncontrolledChecked, setUncontrolledChecked] = React.useState(false);

    // Use controlled checked if provided, otherwise use uncontrolled
    const isControlled = controlledChecked !== undefined;
    const checked = isControlled ? controlledChecked : uncontrolledChecked;

    const handleInputChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      const newChecked = e.target.checked;

      // Update uncontrolled state if not controlled
      if (!isControlled) {
        setUncontrolledChecked(newChecked);
      }

      // Call the callbacks
      if (onCheckedChange) {
        onCheckedChange(newChecked);
      }
      if (onChange) {
        onChange(e);
      }
    }, [isControlled, onCheckedChange, onChange]);

    const handleDivClick = React.useCallback((e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();

      // Toggle the hidden input
      if (resolvedRef && typeof resolvedRef === 'object' && resolvedRef.current) {
        const input = resolvedRef.current;
        input.checked = !input.checked;

        // Trigger change event
        const event = new Event('change', { bubbles: true });
        input.dispatchEvent(event);
      }
    }, [resolvedRef]);

    const handleKeyDown = React.useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();

        // Toggle the hidden input
        if (resolvedRef && typeof resolvedRef === 'object' && resolvedRef.current) {
          const input = resolvedRef.current;
          input.checked = !input.checked;

          // Trigger change event
          const event = new Event('change', { bubbles: true });
          input.dispatchEvent(event);
        }
      }
    }, [resolvedRef]);

    // Sync the hidden input's checked state with the prop (for controlled mode)
    React.useEffect(() => {
      if (resolvedRef && typeof resolvedRef === 'object' && resolvedRef.current) {
        resolvedRef.current.checked = !!checked;
      }
    }, [checked, resolvedRef]);

    // Add native change listener to catch programmatic changes
    React.useEffect(() => {
      const input = resolvedRef && typeof resolvedRef === 'object' && resolvedRef.current;
      if (!input) return;

      const nativeChangeHandler = (e: Event) => {
        const target = e.target as HTMLInputElement;
        handleInputChange({
          target,
          currentTarget: target,
          preventDefault: () => {},
          stopPropagation: () => {},
        } as React.ChangeEvent<HTMLInputElement>);
      };

      // Use capture phase to ensure we catch all change events
      input.addEventListener('change', nativeChangeHandler, true);

      return () => {
        input.removeEventListener('change', nativeChangeHandler, true);
      };
    }, [handleInputChange, resolvedRef]);

    return (
      <label className="relative inline-flex items-center cursor-pointer">
        <input
          type="checkbox"
          ref={resolvedRef}
          className="sr-only"
          checked={checked}
          onChange={handleInputChange}
          {...props}
        />
        <div
          className={`
            w-4 h-4 rounded border-2
            flex items-center justify-center cursor-pointer
            transition-all duration-200
            ${checked
              ? 'bg-blue-600 border-blue-600'
              : 'bg-white border-gray-300 hover:border-gray-400'
            }
            ${className || ''}
          `}
          onClick={handleDivClick}
          role="checkbox"
          aria-checked={checked}
          tabIndex={0}
          onKeyDown={handleKeyDown}
        >
          {checked && (
            <Check className="w-3 h-3 text-white" />
          )}
        </div>
      </label>
    );
  }
);

Checkbox.displayName = "Checkbox";

export { Checkbox }